/**
 * Game Launch Utilities
 * 
 * Handles game launch status logic including coming soon modal behavior
 */

import { LaunchStatus } from '@mental-mint/shared-types';
import { getEnvironment } from './Analytics/EnvironmentUtils';
import { ENVIRONMENTS } from './Analytics/AnalyticsConstants';

/**
 * Determines if a "coming soon" modal should be shown instead of the game.
 * 
 * Logic:
 * - In development: Show coming soon modal for UNANNOUNCED and ANNOUNCED games (with bypass button)
 * - In production: Show coming soon modal for UNANNOUNCED and ANNOUNCED games (no bypass)
 *   - UNANNOUNCED: Show coming soon modal
 *   - ANNOUNCED: Show coming soon modal
 *   - RELEASED: Don't show coming soon modal (game is playable)
 * 
 * @param launchStatus - The launch status of the game
 * @returns true if coming soon modal should be shown, false otherwise
 */
export const shouldShowComingSoonModal = (launchStatus: LaunchStatus): boolean => {
  // Show coming soon modal for ANNOUNCED games in any environment
  // Unnanounced games do not show this modal, since users shouldn't be able to find their URL, and this simplifies testing.
  return launchStatus === LaunchStatus.ANNOUNCED;
};

/**
 * Determines if a game should be shown in the "More Games" section
 * 
 * Logic:
 * - UNANNOUNCED: Don't show in more games
 * - ANNOUNCED: Show in more games
 * - RELEASED: Show in more games
 * 
 * @param launchStatus - The launch status of the game
 * @returns true if game should appear in more games section, false otherwise
 */
export const shouldShowInMoreGames = (launchStatus: LaunchStatus): boolean => {
  return launchStatus === LaunchStatus.ANNOUNCED || launchStatus === LaunchStatus.RELEASED;
};

/**
 * Determines if the game description should be shown (vs "coming soon" text)
 * 
 * Logic:
 * - UNANNOUNCED: N/A (doesn't appear in more games)
 * - ANNOUNCED: Don't show description (show "coming soon" instead)
 * - RELEASED: Show description
 * 
 * @param launchStatus - The launch status of the game
 * @returns true if description should be shown, false if "coming soon" should be shown
 */
export const shouldShowGameDescription = (launchStatus: LaunchStatus): boolean => {
  return launchStatus === LaunchStatus.RELEASED;
};
